import { QueryFunctionContext, QueryKey, useMutation, useQuery } from 'react-query'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import produce, { Immutable, castDraft, castImmutable } from 'immer'
import { DateTime } from 'luxon'
import { DONE_FOLDER_ID, TASK_MARK_AS_DONE_TIMEOUT, TRASH_FOLDER_ID } from '../../constants'
import useQueryContext from '../../context/QueryContext'
import { useNavigateToTask } from '../../hooks'
import apiClient from '../../utils/api'
import navigateToNextItemAfterOverviewCompletion from '../../utils/navigateToNextItemAfterOverviewCompletion'
import { isTaskOverviewView } from '../../utils/overview'
import {
    TOverviewView,
    TTaskFolder,
    TTaskRecurrenceSuggestion,
    TTaskResearch,
    TTaskV4,
    isTaskResearchActive,
} from '../../utils/types'
import { resetOrderingIds, sleep } from '../../utils/utils'
import {
    OptimisticTransaction,
    beginOptimisticTransaction,
    optimisticQueryChange,
    reconcileOptimisticTransaction,
    rollbackOptimisticTransaction,
} from '../optimisticTransaction'
import { GTQueryClient, getBackgroundQueryOptions, useGTMutation, useGTQueryClient } from '../queryUtils'

export interface TCreateTaskData {
    title: string
    body?: string
    dueDate?: string
    id_folder?: string
    id_parent?: string
    optimisticId: string
}

export interface TCreateTaskResponse {
    task_id: string
}

export interface TModifyTaskData {
    id: string
    title?: string
    dueDate?: string
    body?: string
    priorityNormalized?: number
    recurringTaskTemplateId?: string
}

interface TTaskModifyRequestBody {
    task: {
        priority_normalized?: number
        recurring_task_template_id?: string
    }
    id_task_section?: string
    id_ordering?: number
    title?: string
    due_date?: string
    time_duration?: number
    body?: string
}

export interface TMarkTaskDoneOrDeletedData {
    id: string
    isDone?: boolean
    isDeleted?: boolean
    waitForAnimation?: boolean
}

interface TMarkTasksDoneOrDeletedData extends Omit<TMarkTaskDoneOrDeletedData, 'id'> {
    ids: string[]
}

type TMarkTaskDoneOrDeletedVariables = TMarkTaskDoneOrDeletedData | TMarkTasksDoneOrDeletedData

interface TMarkTaskDoneOrDeletedRequestBody {
    is_completed?: boolean
    is_deleted?: boolean
}

export interface TReorderTaskData {
    id: string
    isSubtask?: boolean
    parentId?: string
    dropSectionId: string
    orderingId: number
    dragSectionId?: string
}
interface TReorderTaskRequestBody {
    id_task_section: string
    id_ordering: number
    is_completed?: boolean
    is_deleted?: boolean
}
export const useGetTasksV4 = (isEnabled = true) => {
    return useQuery<TTaskV4[], void>('tasks_v4', getTasksV4, { enabled: isEnabled, refetchOnMount: false })
}
const getTasksV4 = async ({ signal }: QueryFunctionContext) => {
    try {
        const res = await apiClient.get('/tasks/v4/', { signal })
        return castImmutable(res.data)
    } catch {
        throw 'getTasks failed'
    }
}

const reconcileTaskResearch = (queryClient: GTQueryClient, taskId: string, research: TTaskResearch | null) => {
    const tasks = queryClient.getImmutableQueryData<TTaskV4[]>('tasks_v4')
    if (!tasks) return
    queryClient.setQueryData<TTaskV4[]>(
        'tasks_v4',
        produce(tasks, (draft) => {
            const task = draft.find((candidate) => candidate.id === taskId)
            if (!task) return
            if (research) task.research = research
            else delete task.research
        }) as TTaskV4[]
    )
}

export const useGetTaskResearch = (taskId: string, enabled: boolean) => {
    const queryClient = useGTQueryClient()
    return useQuery<TTaskResearch | null, unknown>(['task-research', taskId], () => getTaskResearch(taskId), {
        enabled,
        refetchInterval: (research) => (isTaskResearchActive(research) ? 1500 : false),
        onSuccess: (research) => reconcileTaskResearch(queryClient, taskId, research),
    })
}

const getTaskResearch = async (taskId: string) => {
    const res = await apiClient.get(`/tasks/research/${taskId}/`)
    return res.data as TTaskResearch | null
}

export const useStartTaskResearch = (taskId: string) => {
    const queryClient = useGTQueryClient()
    return useMutation<TTaskResearch, TTaskResearchStartError, string>(
        (question: string) => startTaskResearch(taskId, question),
        {
            onSuccess: (research) => {
                queryClient.setQueryData(['task-research', taskId], research)
                reconcileTaskResearch(queryClient, taskId, research)
            },
        }
    )
}

export interface TTaskResearchStartError {
    status?: number
    code?: string
}

const startTaskResearch = async (taskId: string, question: string) => {
    try {
        const res = await apiClient.post(`/tasks/research/${taskId}/`, { question })
        return res.data as TTaskResearch
    } catch (error) {
        if (axios.isAxiosError(error)) {
            const code = error.response?.data?.code
            throw {
                status: error.response?.status,
                code: typeof code === 'string' ? code : undefined,
            } as TTaskResearchStartError
        }
        throw {} as TTaskResearchStartError
    }
}

const updateRecurrenceSuggestion = (
    tasks: Immutable<TTaskV4[]>,
    taskId: string,
    update: (suggestion: TTaskRecurrenceSuggestion | undefined) => TTaskRecurrenceSuggestion | undefined
) =>
    produce(tasks, (draft) => {
        const task = draft.find((candidate) => candidate.id === taskId)
        if (!task) return
        const suggestion = update(task.recurrence_suggestion)
        if (suggestion) task.recurrence_suggestion = suggestion
        else delete task.recurrence_suggestion
    }) as TTaskV4[]

const reconcileRecurrenceSuggestion = (
    queryClient: GTQueryClient,
    taskId: string,
    suggestion: TTaskRecurrenceSuggestion
) => {
    const tasks = queryClient.getImmutableQueryData<TTaskV4[]>('tasks_v4')
    if (tasks)
        queryClient.setQueryData<TTaskV4[]>(
            'tasks_v4',
            updateRecurrenceSuggestion(tasks, taskId, () => suggestion)
        )
}

export const useEvaluateRecurrenceSuggestion = (taskId: string) => {
    const queryClient = useGTQueryClient()
    return useMutation<TTaskRecurrenceSuggestion, unknown>(() => evaluateRecurrenceSuggestion(taskId), {
        onSuccess: (suggestion) => reconcileRecurrenceSuggestion(queryClient, taskId, suggestion),
    })
}

const evaluateRecurrenceSuggestion = async (taskId: string) => {
    const response = await apiClient.post(`/tasks/recurrence_suggestion/${taskId}/evaluate/`)
    return response.data as TTaskRecurrenceSuggestion
}

export const useDismissRecurrenceSuggestion = (taskId: string) => {
    const queryClient = useGTQueryClient()
    return useMutation<TTaskRecurrenceSuggestion, unknown, void, OptimisticTransaction<TTaskRecurrenceSuggestion>>(
        () => dismissRecurrenceSuggestion(taskId),
        {
            onMutate: () => {
                void queryClient.cancelQueries('tasks_v4')
                return beginOptimisticTransaction(queryClient, [
                    optimisticQueryChange<TTaskV4[], TTaskRecurrenceSuggestion>(
                        'tasks_v4',
                        (current) =>
                            current
                                ? updateRecurrenceSuggestion(current, taskId, (suggestion) =>
                                      suggestion ? { ...suggestion, status: 'dismissed' } : suggestion
                                  )
                                : current,
                        {
                            rollback: (current, previous, optimistic) => {
                                if (!current || !previous || !optimistic) return current
                                const previousStatus = previous.find((task) => task.id === taskId)
                                    ?.recurrence_suggestion?.status
                                const optimisticStatus = optimistic.find((task) => task.id === taskId)
                                    ?.recurrence_suggestion?.status
                                if (!previousStatus || !optimisticStatus) return current
                                return updateRecurrenceSuggestion(current, taskId, (suggestion) =>
                                    suggestion?.status === optimisticStatus
                                        ? { ...suggestion, status: previousStatus }
                                        : suggestion
                                )
                            },
                            reconcile: (current, suggestion) =>
                                current ? updateRecurrenceSuggestion(current, taskId, () => suggestion) : current,
                        }
                    ),
                ])
            },
            onSuccess: (suggestion, _variables, transaction) => reconcileOptimisticTransaction(transaction, suggestion),
            onError: (_error, _variables, transaction) => rollbackOptimisticTransaction(transaction),
        }
    )
}

const dismissRecurrenceSuggestion = async (taskId: string) => {
    const response = await apiClient.post(`/tasks/recurrence_suggestion/${taskId}/dismiss/`)
    return response.data as TTaskRecurrenceSuggestion
}

export const useFetchExternalTasks = (isEnabled = true) => {
    const queryClient = useGTQueryClient()
    return useQuery('tasksExternal', fetchExternalTasks, {
        enabled: isEnabled,
        onSettled: () => {
            queryClient.invalidateQueries('tasks_v4')
            queryClient.invalidateQueries('folders')
            queryClient.invalidateQueries('overview')
        },
        ...getBackgroundQueryOptions(),
    })
}
const fetchExternalTasks = async ({ signal }: QueryFunctionContext) => {
    try {
        const res = await apiClient.get('/tasks/fetch/', { signal })
        return castImmutable(res.data)
    } catch {
        throw 'fetchExternalTasks failed'
    }
}

export const useCreateTask = () => {
    const queryClient = useGTQueryClient()
    const { setOptimisticId } = useQueryContext()
    const navigateToTask = useNavigateToTask()

    return useGTMutation((data: TCreateTaskData) => createTask(data), {
        tag: 'tasks_v4',
        invalidateTagsOnSettled: ['tasks_v4', 'folders', 'overview'],
        errorMessage: 'create task',
        onMutate: async (data: TCreateTaskData) => {
            await Promise.all([
                queryClient.cancelQueries('tasks_v4'),
                queryClient.cancelQueries('folders'),
                queryClient.cancelQueries('overview'),
            ])
            const tasks_v4 = queryClient.getImmutableQueryData<TTaskV4[]>('tasks_v4')
            if (tasks_v4) {
                const updatedTasks = produce(tasks_v4, (draft) => {
                    const newTask = createNewTaskV4Helper(data)
                    draft.unshift(newTask)
                    if (data.id_parent) {
                        const parentTask = draft.find((task) => task.id === data.id_parent)
                        if (!parentTask) return
                        parentTask.subtask_ids = [data.optimisticId, ...(parentTask.subtask_ids || [])]
                    }
                })
                queryClient.setQueryData('tasks_v4', updatedTasks)
            }

            const folders = queryClient.getImmutableQueryData<TTaskFolder[]>('folders')
            if (folders) {
                const updatedFolders = produce(folders, (draft) => {
                    const folder = draft.find((folder) => folder.id === data.id_folder)
                    if (!folder) return
                    folder.task_ids.unshift(data.optimisticId)
                })
                queryClient.setQueryData('folders', updatedFolders)
            }

            const lists = queryClient.getImmutableQueryData<TOverviewView[]>('overview')
            if (lists) {
                const updatedLists = produce(lists, (draft) => {
                    draft.forEach((view) => {
                        const isDestinationFolder =
                            view.type === 'task_section' && view.task_section_id === data.id_folder
                        const isDueTodayDestination =
                            view.type === 'due_today' &&
                            !!data.dueDate &&
                            DateTime.fromISO(data.dueDate).hasSame(DateTime.local(), 'day')
                        if (
                            (isDestinationFolder || isDueTodayDestination) &&
                            !view.view_item_ids.includes(data.optimisticId)
                        )
                            view.view_item_ids.unshift(data.optimisticId)
                    })
                })
                queryClient.setQueryData('overview', updatedLists)
            }
        },
        onSuccess: async (response: TCreateTaskResponse, createData: TCreateTaskData) => {
            setOptimisticId(createData.optimisticId, response.task_id)

            const tasks = queryClient.getImmutableQueryData<TTaskV4[]>('tasks_v4')
            const updatedTasks = produce(tasks, (draft) => {
                const task = draft?.find((task) => task.id === createData.optimisticId)
                if (!task) return
                task.id = response.task_id
                task.optimisticId = undefined
            })
            if (updatedTasks) queryClient.setQueryData('tasks_v4', updatedTasks)

            const folders = queryClient.getImmutableQueryData<TTaskFolder[]>('folders')
            const updatedFolders = produce(folders, (draft) => {
                const folder = draft?.find((folder) => folder.id === createData.id_folder)
                if (!folder) return
                const taskIdIndex = folder.task_ids.indexOf(createData.optimisticId)
                if (taskIdIndex === -1) return
                folder.task_ids[taskIdIndex] = response.task_id
            })
            if (updatedFolders) queryClient.setQueryData('folders', updatedFolders)

            const lists = queryClient.getImmutableQueryData<TOverviewView[]>('overview')
            const updatedLists = produce(lists, (draft) => {
                for (const list of draft?.filter(isTaskOverviewView) ?? []) {
                    const seenIds = new Set<string>()
                    list.view_item_ids = list.view_item_ids
                        .map((id) => (id === createData.optimisticId ? response.task_id : id))
                        .filter((id) => {
                            if (seenIds.has(id)) return false
                            seenIds.add(id)
                            return true
                        })

                    const seenItems = new Set<string>()
                    list.view_items = list.view_items
                        .map((item) => {
                            if (item.id === createData.optimisticId) item.id = response.task_id
                            return item
                        })
                        .filter((item) => {
                            if (seenItems.has(item.id)) return false
                            seenItems.add(item.id)
                            return true
                        })
                }
            })
            if (lists) queryClient.setQueryData('overview', updatedLists)

            if (window.location.pathname.includes(createData.optimisticId)) {
                navigateToTask({
                    taskId: response.task_id,
                    tasks: updatedTasks as TTaskV4[],
                    folders: updatedFolders as TTaskFolder[],
                    views: updatedLists as TOverviewView[],
                })
            }
        },
        onError: (_error: unknown, createData: TCreateTaskData) => {
            const tasks = queryClient.getImmutableQueryData<TTaskV4[]>('tasks_v4')
            if (tasks)
                queryClient.setQueryData<TTaskV4[]>(
                    'tasks_v4',
                    tasks
                        .filter((task) => task.id !== createData.optimisticId)
                        .map((task) => ({
                            ...task,
                            subtask_ids: task.subtask_ids?.filter((id) => id !== createData.optimisticId),
                        })) as TTaskV4[]
                )

            const folders = queryClient.getImmutableQueryData<TTaskFolder[]>('folders')
            if (folders)
                queryClient.setQueryData<TTaskFolder[]>(
                    'folders',
                    folders.map((folder) => ({
                        ...folder,
                        task_ids: folder.task_ids.filter((id) => id !== createData.optimisticId),
                    })) as TTaskFolder[]
                )

            const views = queryClient.getImmutableQueryData<TOverviewView[]>('overview')
            if (views)
                queryClient.setQueryData<TOverviewView[]>(
                    'overview',
                    views.map((view) => ({
                        ...view,
                        view_item_ids: view.view_item_ids.filter((id) => id !== createData.optimisticId),
                    })) as TOverviewView[]
                )
        },
    })
}
export const createTask = async (data: TCreateTaskData) => {
    try {
        const dueDate = data.dueDate
            ? DateTime.fromISO(data.dueDate, { zone: 'utc' }).startOf('day').toISO()
            : undefined
        const res = await apiClient.post('/tasks/create/gt_task/', {
            title: data.title,
            body: data.body ?? '',
            due_date: dueDate,
            id_task_section: data.id_folder,
            parent_task_id: data.id_parent,
        })
        return castImmutable(res.data)
    } catch {
        throw 'createTask failed'
    }
}

const optimisticallyUpdateTask = (queryClient: GTQueryClient, data: TModifyTaskData, queryKey: QueryKey) => {
    const queryData = queryClient.getImmutableQueryData<TTaskV4[]>(queryKey)
    if (!queryData) return

    const updatedTasks = produce(queryData, (draft) => {
        const task = draft.find((task) => task.id === data.id)
        if (!task) return
        task.title = data.title || task.title
        if (data.dueDate === '1969-12-31') {
            task.due_date = ''
        } else {
            task.due_date = data.dueDate ?? task.due_date
        }
        task.body = data.body ?? task.body
        task.priority_normalized = data.priorityNormalized ?? task.priority_normalized
        task.recurring_task_template_id = data.recurringTaskTemplateId ?? task.recurring_task_template_id
        task.updated_at = DateTime.utc().toISO()
    })
    queryClient.setQueryData(queryKey, updatedTasks)
}

const isTaskInDueTodayView = (task: Immutable<TTaskV4>, now = DateTime.local()) => {
    if (task.is_done || task.is_deleted || task.id_parent || !task.due_date) return false
    const dueDate = DateTime.fromISO(task.due_date)
    // The backend's Due Today view includes overdue work as well as work due on the local day.
    return dueDate.isValid && dueDate.year > 1971 && dueDate.startOf('day') <= now.endOf('day')
}

const dueDateMillis = (task: Immutable<TTaskV4> | undefined) => {
    if (!task?.due_date) return Number.POSITIVE_INFINITY
    const dueDate = DateTime.fromISO(task.due_date)
    return dueDate.isValid ? dueDate.startOf('day').toMillis() : Number.POSITIVE_INFINITY
}

const syncTaskWithDueTodayViews = (queryClient: GTQueryClient, taskId: string) => {
    const tasks = queryClient.getImmutableQueryData<TTaskV4[]>('tasks_v4')
    const views = queryClient.getImmutableQueryData<TOverviewView[]>('overview')
    const task = tasks?.find((candidate) => candidate.id === taskId)
    if (!tasks || !views || !task) return

    const tasksById = new Map(tasks.map((candidate) => [candidate.id, candidate]))
    const shouldInclude = isTaskInDueTodayView(task)
    const updatedViews = produce(views, (draft) => {
        for (const view of draft) {
            if (view.type !== 'due_today') continue

            const existingItems = new Map(view.view_items.map((item) => [item.id, item]))
            const seen = new Set<string>()
            const ids = view.view_item_ids.filter((id) => {
                if (id === taskId && !shouldInclude) return false
                if (seen.has(id)) return false
                seen.add(id)
                return true
            })
            if (shouldInclude && !seen.has(taskId)) ids.push(taskId)

            // The server orders this view by due date. Stable tie ordering keeps existing rows still
            // while placing a newly-due task after the other tasks for the same day.
            const originalIndex = new Map(ids.map((id, index) => [id, index]))
            ids.sort(
                (a, b) =>
                    dueDateMillis(tasksById.get(a)) - dueDateMillis(tasksById.get(b)) ||
                    (originalIndex.get(a) ?? 0) - (originalIndex.get(b) ?? 0)
            )

            view.view_item_ids = ids
            view.view_items = ids.flatMap((id) => {
                const item = tasksById.get(id) ?? existingItems.get(id)
                return item ? [castDraft(item)] : []
            })
        }
    })
    queryClient.setQueryData('overview', updatedViews)
}

interface TDueTodayMembershipSnapshot {
    viewId: string
    index: number
}

interface TModifyTaskContext {
    kind: 'modify-task'
    data: TModifyTaskData
    task?: Immutable<TTaskV4>
    optimisticTask?: Immutable<TTaskV4>
    meetingTask?: Immutable<TTaskV4>
    optimisticMeetingTask?: Immutable<TTaskV4>
    dueTodayMembership: TDueTodayMembershipSnapshot[]
}

const rollbackTaskEdit = (
    queryClient: GTQueryClient,
    queryKey: QueryKey,
    taskId: string,
    snapshot?: Immutable<TTaskV4>,
    optimisticSnapshot?: Immutable<TTaskV4>
) => {
    if (!snapshot || !optimisticSnapshot) return
    const tasks = queryClient.getImmutableQueryData<TTaskV4[]>(queryKey)
    if (!tasks) return
    queryClient.setQueryData(
        queryKey,
        produce(tasks, (draft) => {
            const task = draft.find((candidate) => candidate.id === taskId)
            if (!task) return

            // Restore only values still owned by this failed edit. Other operations share the
            // tasks_v4 queue, so replacing the whole task would erase a later completion or move.
            if (task.title === optimisticSnapshot.title) task.title = snapshot.title
            if (task.due_date === optimisticSnapshot.due_date) task.due_date = snapshot.due_date
            if (task.body === optimisticSnapshot.body) task.body = snapshot.body
            if (task.priority_normalized === optimisticSnapshot.priority_normalized) {
                task.priority_normalized = snapshot.priority_normalized
            }
            if (task.recurring_task_template_id === optimisticSnapshot.recurring_task_template_id) {
                task.recurring_task_template_id = snapshot.recurring_task_template_id
            }
            if (task.updated_at === optimisticSnapshot.updated_at) task.updated_at = snapshot.updated_at
        })
    )
}

const restoreDueTodayMembership = (
    queryClient: GTQueryClient,
    taskId: string,
    membership: TDueTodayMembershipSnapshot[]
) => {
    const views = queryClient.getImmutableQueryData<TOverviewView[]>('overview')
    if (!views) return
    const membershipByView = new Map(membership.map((snapshot) => [snapshot.viewId, snapshot.index]))
    queryClient.setQueryData(
        'overview',
        produce(views, (draft) => {
            for (const view of draft) {
                if (view.type !== 'due_today') continue
                view.view_item_ids = view.view_item_ids.filter((id) => id !== taskId)
                view.view_items = view.view_items.filter((item) => item.id !== taskId)
                const index = membershipByView.get(view.id)
                if (index !== undefined && index >= 0) {
                    view.view_item_ids.splice(Math.min(index, view.view_item_ids.length), 0, taskId)
                }
            }
        })
    )
}

export const useModifyTask = (useQueueing = true) => {
    const queryClient = useGTQueryClient()
    const { getQueryQueue } = useQueryContext()

    return useGTMutation<unknown, unknown, TModifyTaskData, TModifyTaskContext>(
        (data: TModifyTaskData) => modifyTask(data),
        {
            tag: 'tasks_v4',
            invalidateTagsOnSettled: ['tasks_v4', 'overview', 'folders', 'meeting_preparation_tasks'],
            errorMessage: 'modify task',
            onMutate: async (data: TModifyTaskData) => {
                await Promise.all([
                    queryClient.cancelQueries('overview'),
                    queryClient.cancelQueries('folders'),
                    queryClient.cancelQueries('tasks_v4'),
                    queryClient.cancelQueries('meeting_preparation_tasks'),
                ])
                const tasks = queryClient.getImmutableQueryData<TTaskV4[]>('tasks_v4')
                const meetingTasks = queryClient.getImmutableQueryData<TTaskV4[]>('meeting_preparation_tasks')
                const views = queryClient.getImmutableQueryData<TOverviewView[]>('overview')
                const context: TModifyTaskContext = {
                    kind: 'modify-task',
                    data: { ...data },
                    task: tasks?.find((task) => task.id === data.id),
                    meetingTask: meetingTasks?.find((task) => task.id === data.id),
                    dueTodayMembership:
                        views
                            ?.filter((view) => view.type === 'due_today')
                            .map((view) => ({ viewId: view.id, index: view.view_item_ids.indexOf(data.id) })) ?? [],
                }
                optimisticallyUpdateTask(queryClient, data, 'tasks_v4')
                optimisticallyUpdateTask(queryClient, data, 'meeting_preparation_tasks')
                syncTaskWithDueTodayViews(queryClient, data.id)
                context.optimisticTask = queryClient
                    .getImmutableQueryData<TTaskV4[]>('tasks_v4')
                    ?.find((task) => task.id === data.id)
                context.optimisticMeetingTask = queryClient
                    .getImmutableQueryData<TTaskV4[]>('meeting_preparation_tasks')
                    ?.find((task) => task.id === data.id)
                return context
            },
            onError: (_error, data, context) => {
                // useGTMutation stores the async onMutate result directly in its queue. Capture the
                // later contexts before onSettled shifts the queue, then resolve and replay them so
                // rolling back this request cannot erase newer optimistic edits.
                const queue = getQueryQueue('tasks_v4')
                const currentIndex = queue.findIndex((request) => request.context === context)
                const laterContexts = (currentIndex === -1 ? [] : queue.slice(currentIndex + 1)).map(
                    (request) => request.context
                )
                void Promise.all([
                    Promise.resolve(context),
                    Promise.all(laterContexts.map((item) => Promise.resolve(item))),
                ]).then(([resolved, later]) => {
                    if (!resolved || resolved.kind !== 'modify-task') return
                    rollbackTaskEdit(queryClient, 'tasks_v4', data.id, resolved.task, resolved.optimisticTask)
                    rollbackTaskEdit(
                        queryClient,
                        'meeting_preparation_tasks',
                        data.id,
                        resolved.meetingTask,
                        resolved.optimisticMeetingTask
                    )
                    restoreDueTodayMembership(queryClient, data.id, resolved.dueTodayMembership)

                    for (const pending of later) {
                        const laterModify = pending as TModifyTaskContext | undefined
                        if (laterModify?.kind !== 'modify-task' || laterModify.data.id !== data.id) continue
                        optimisticallyUpdateTask(queryClient, laterModify.data, 'tasks_v4')
                        optimisticallyUpdateTask(queryClient, laterModify.data, 'meeting_preparation_tasks')
                    }
                    syncTaskWithDueTodayViews(queryClient, data.id)
                })
            },
        },
        useQueueing
    )
}
const modifyTask = async (data: TModifyTaskData) => {
    // Format due date to ISO string in format yyyy-MM-dd
    if (data.dueDate) {
        data.dueDate = DateTime.fromISO(data.dueDate).toFormat('yyyy-MM-dd')
    }
    const requestBody: TTaskModifyRequestBody = { task: {} }
    if (data.title !== undefined) requestBody.title = data.title
    if (data.dueDate !== undefined) requestBody.due_date = data.dueDate
    if (data.body !== undefined) requestBody.body = data.body
    if (data.priorityNormalized !== undefined) requestBody.task.priority_normalized = data.priorityNormalized
    if (data.recurringTaskTemplateId !== undefined)
        requestBody.task.recurring_task_template_id = data.recurringTaskTemplateId
    try {
        const res = await apiClient.patch(`/tasks/modify/${data.id}/`, requestBody)
        return castImmutable(res.data)
    } catch {
        throw 'modifyTask failed'
    }
}

export const useMarkTaskDoneOrDeleted = (useQueueing = true) => {
    const queryClient = useGTQueryClient()
    const navigate = useNavigate()

    return useGTMutation(
        (data: TMarkTaskDoneOrDeletedVariables) =>
            'id' in data ? markTaskDoneOrDeleted(data) : markTasksDoneOrDeleted(data),
        {
            tag: 'tasks_v4',
            invalidateTagsOnSettled: ['tasks_v4', 'folders', 'overview', 'goals'],
            errorMessage: 'complete task',
            onMutate: async (data: TMarkTaskDoneOrDeletedVariables) => {
                await Promise.all([
                    queryClient.cancelQueries('tasks_v4'),
                    queryClient.cancelQueries('folders'),
                    queryClient.cancelQueries('overview'),
                    queryClient.cancelQueries('meeting_preparation_tasks'),
                ])
                const taskIds = 'id' in data ? [data.id] : data.ids
                const taskIdSet = new Set(taskIds)
                const animationDelay = data.waitForAnimation ? sleep(TASK_MARK_AS_DONE_TIMEOUT) : Promise.resolve()
                const updateMeetingTasks = async () => {
                    await animationDelay
                    const meetingTasks = queryClient.getImmutableQueryData<TTaskV4[]>('meeting_preparation_tasks')
                    if (!meetingTasks) return
                    const updatedMeetingTasks = produce(meetingTasks, (draft) => {
                        draft.forEach((task) => {
                            if (!taskIdSet.has(task.id)) return
                            if (data.isDone !== undefined) task.is_done = data.isDone
                            if (data.isDeleted !== undefined) task.is_deleted = data.isDeleted
                        })
                    })
                    queryClient.setQueryData('meeting_preparation_tasks', updatedMeetingTasks)
                }

                const updateTasks = async () => {
                    await animationDelay
                    const tasks = queryClient.getImmutableQueryData<TTaskV4[]>('tasks_v4')
                    if (!tasks) return
                    const updatedTasks = produce(tasks, (draft) => {
                        draft.forEach((task) => {
                            if (!taskIdSet.has(task.id)) return
                            if (data.isDone !== undefined) task.is_done = data.isDone
                            if (data.isDeleted !== undefined) task.is_deleted = data.isDeleted
                        })
                    })
                    queryClient.setQueryData('tasks_v4', updatedTasks)
                }

                const updateFolders = async () => {
                    await animationDelay
                    const folders = queryClient.getImmutableQueryData<TTaskFolder[]>('folders')
                    if (!folders) return
                    const updatedFolders = produce(folders, (draft) => {
                        const doneFolder = draft.find((folder) => folder.id === DONE_FOLDER_ID)
                        const trashFolder = draft.find((folder) => folder.id === TRASH_FOLDER_ID)
                        if (!doneFolder || !trashFolder) return
                        taskIds.forEach((taskId) => {
                            const currentFolder = draft.find((folder) => folder.task_ids.includes(taskId))
                            if (!currentFolder) return
                            if (data.isDone !== undefined) {
                                if (data.isDone && !currentFolder.is_done) {
                                    currentFolder.task_ids = currentFolder.task_ids.filter((id) => id !== taskId)
                                    doneFolder.task_ids.unshift(taskId)
                                } else if (!data.isDone && currentFolder.is_done) {
                                    doneFolder.task_ids = doneFolder.task_ids.filter((id) => id !== taskId)
                                    currentFolder.task_ids.unshift(taskId)
                                }
                            }
                            if (data.isDeleted !== undefined) {
                                if (data.isDeleted && !currentFolder.is_trash) {
                                    currentFolder.task_ids = currentFolder.task_ids.filter((id) => id !== taskId)
                                    trashFolder.task_ids.unshift(taskId)
                                } else if (!data.isDeleted && currentFolder.is_trash) {
                                    trashFolder.task_ids = trashFolder.task_ids.filter((id) => id !== taskId)
                                    currentFolder.task_ids.unshift(taskId)
                                }
                            }
                        })
                    })
                    queryClient.setQueryData('folders', updatedFolders)
                }

                const updateLists = async () => {
                    await animationDelay
                    const lists = queryClient.getImmutableQueryData<TOverviewView[]>('overview')
                    if (!lists) return
                    const updatedLists = produce(lists, (draft) => {
                        const currentLists = draft.filter((list) => list.view_item_ids.some((id) => taskIdSet.has(id)))
                        if (data.isDone || data.isDeleted) {
                            for (const list of currentLists) {
                                list.view_item_ids = list.view_item_ids.filter((id) => !taskIdSet.has(id))
                                if (list.view_item_ids.length === 0) {
                                    list.has_tasks_completed_today = true
                                }
                            }
                        }
                    })

                    queryClient.setQueryData('overview', updatedLists)

                    if (window.location.pathname.split('/')[1] !== 'overview') return
                    const selectedTaskId = window.location.pathname.split('/')[3]
                    const navigationTaskId = taskIds.includes(selectedTaskId) ? selectedTaskId : taskIds[0]
                    if (!lists.some((list) => list.view_item_ids.includes(navigationTaskId))) return
                    navigateToNextItemAfterOverviewCompletion(
                        lists as TOverviewView[],
                        updatedLists as TOverviewView[],
                        navigationTaskId,
                        navigate
                    )
                }
                // execute in parallel if waiting for animation delay
                updateFolders()
                updateTasks()
                updateLists()
                updateMeetingTasks()
            },
        },
        useQueueing
    )
}
export const markTaskDoneOrDeleted = async (data: TMarkTaskDoneOrDeletedData) => {
    const requestBody: TMarkTaskDoneOrDeletedRequestBody = {}
    if (data.isDone !== undefined) requestBody.is_completed = data.isDone
    if (data.isDeleted !== undefined) requestBody.is_deleted = data.isDeleted
    try {
        const res = await apiClient.patch(`/tasks/modify/${data.id}/`, requestBody)
        return castImmutable(res.data)
    } catch {
        throw 'markTaskDoneOrDeleted failed'
    }
}

const markTasksDoneOrDeleted = async (data: TMarkTasksDoneOrDeletedData) => {
    const results = await Promise.allSettled(
        data.ids.map((id) =>
            markTaskDoneOrDeleted({
                id,
                isDone: data.isDone,
                isDeleted: data.isDeleted,
            })
        )
    )

    return results.map((result) => {
        if (result.status === 'rejected') throw result.reason
        return result.value
    })
}

export const useReorderTask = (useQueueing = true) => {
    const queryClient = useGTQueryClient()
    return useGTMutation(
        (data: TReorderTaskData) => reorderTask(data),
        {
            tag: 'tasks_v4',
            invalidateTagsOnSettled: ['tasks_v4', 'folders', 'overview'],
            errorMessage: 'move task',
            onMutate: async (data: TReorderTaskData) => {
                await Promise.all([
                    queryClient.cancelQueries('tasks_v4'),
                    queryClient.cancelQueries('folders'),
                    queryClient.cancelQueries('overview'),
                ])

                const tasks = queryClient.getImmutableQueryData<TTaskV4[]>('tasks_v4')
                if (tasks) {
                    const updatedTasks = produce(tasks, (draft) => {
                        const task = draft.find((task) => task.id === data.id)
                        if (!task) return
                        if (task.id_parent) {
                            task.id_ordering = data.orderingId
                            const parentSubtasks = draft
                                .filter((task) => task.id_parent === data.parentId)
                                .sort((a, b) => {
                                    if (a.id_ordering === b.id_ordering) return task.id === a.id ? -1 : 1
                                    return a.id_ordering - b.id_ordering
                                })
                            resetOrderingIds(parentSubtasks)
                        } else {
                            task.id_ordering = data.orderingId
                            task.id_folder = data.dropSectionId
                            task.is_done = data.dropSectionId === DONE_FOLDER_ID
                            task.is_deleted = data.dropSectionId === TRASH_FOLDER_ID
                            const dropFolder = draft
                                .filter((task) => task.id_folder === data.dropSectionId)
                                .sort((a, b) => {
                                    if (a.id_ordering === b.id_ordering) return task.id === a.id ? -1 : 1
                                    return a.id_ordering - b.id_ordering
                                })
                            resetOrderingIds(dropFolder)
                        }
                    })
                    queryClient.setQueryData('tasks_v4', updatedTasks)
                }

                if (!data.dragSectionId || data.dropSectionId === data.dragSectionId) return

                const folders = queryClient.getImmutableQueryData<TTaskFolder[]>('folders')
                if (folders) {
                    const updatedFolders = produce(folders, (draft) => {
                        const previousFolder = draft.find((folder) => folder.id === data.dragSectionId)
                        const newFolder = draft.find((folder) => folder.id === data.dropSectionId)
                        if (!previousFolder || !newFolder) return
                        previousFolder.task_ids = previousFolder.task_ids.filter((id) => id !== data.id)
                        newFolder.task_ids.unshift(data.id)
                    })
                    queryClient.setQueryData('folders', updatedFolders)
                }

                const lists = queryClient.getImmutableQueryData<TOverviewView[]>('overview')
                if (lists) {
                    const updatedLists = produce(lists, (draft) => {
                        const previousList = draft.find(
                            (list) => isTaskOverviewView(list) && list.task_section_id === data.dragSectionId
                        )
                        const newList = draft.find(
                            (list) => isTaskOverviewView(list) && list.task_section_id === data.dropSectionId
                        )
                        if (!previousList || !newList) return
                        previousList.view_item_ids = previousList.view_item_ids.filter((id) => id !== data.id)
                        newList.view_item_ids.unshift(data.id)
                    })
                    queryClient.setQueryData('overview', updatedLists)
                }
            },
        },
        useQueueing
    )
}

export const reorderTask = async (data: TReorderTaskData) => {
    try {
        const requestBody: TReorderTaskRequestBody = {
            id_task_section: data.dropSectionId,
            id_ordering: data.orderingId,
            is_completed: data.isSubtask ? undefined : data.dropSectionId === DONE_FOLDER_ID,
        }
        const res = await apiClient.patch(`/tasks/modify/${data.id}/`, requestBody)
        return castImmutable(res.data)
    } catch {
        throw 'reorderTask failed'
    }
}

export const createNewTaskV4Helper = (
    data: Partial<TTaskV4> & { optimisticId: string; title: string; dueDate?: string }
): TTaskV4 => {
    return {
        id: data.optimisticId,
        optimisticId: data.optimisticId,
        id_ordering: data.id_ordering ?? 0.5,
        title: data.title,
        deeplink: data.deeplink ?? '',
        body: data.body ?? '',
        priority_normalized: data.priority_normalized ?? 0,
        due_date: data.dueDate ?? data.due_date ?? '',
        source: data.source ?? {
            name: 'General Task',
            logo: 'generaltask',
        },
        sender: data.sender ?? '',
        is_done: data.is_done ?? false,
        is_deleted: data.is_deleted ?? false,
        created_at: data.created_at ?? DateTime.utc().toISO(),
        updated_at: data.updated_at ?? DateTime.utc().toISO(),
        deleted_at: data.deleted_at ?? DateTime.utc().toISO(),
        completed_at: data.completed_at ?? DateTime.utc().toISO(),
        id_folder: data.id_folder ?? undefined,
        id_nux_number: data.id_nux_number,
        id_parent: data.id_parent,
        subtask_ids: data.subtask_ids,
        meeting_preparation_params: data.meeting_preparation_params,
        research: data.research,
    }
}
